import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

type OnPathChangeCallback = (path: string) => void;

/**
 * A React hook that exposes a callback for client-side route changes
 * using React Router's useLocation hook.
 *
 * @param callback The function to call when the path changes. It receives the new path as an argument.
 */
export const useOnPathChange = (callback: OnPathChangeCallback) => {
  const location = useLocation();
  const latestCallback = useRef(callback);

  // Update the ref whenever the callback changes
  useEffect(() => {
    latestCallback.current = callback;
  }, [callback]);

  // Effect to run when the location object changes
  useEffect(() => {
    // The location object includes pathname, search, hash, etc.
    // We're interested in pathname for "onPathChange"
    latestCallback.current(location.pathname);
  }, [location]); // Depend on the entire location object, or location.pathname if you only care about path changes
};
